import { getPlatform } from "@/lib/platform";
import { ApiError, SchemaError, UnauthorizedError, rawFetch } from "./client";
import { SessionEnvelopeSchema, type SessionEnvelope } from "@/store/session";
import { REFRESH_TOKEN_KEY } from "@/lib/storageKeys";

function parseEnvelope(body: unknown): SessionEnvelope {
  const parsed = SessionEnvelopeSchema.safeParse(body);
  if (!parsed.success) {
    throw new SchemaError(parsed.error.issues);
  }
  return parsed.data;
}

async function persistRefreshToken(
  refreshToken: SessionEnvelope["refresh_token"],
): Promise<void> {
  // Both admin-web auth endpoints are documented to always return a
  // rotated token; the type-narrowing guard is defense against a schema
  // that allows null/undefined, not an expected runtime path.
  if (typeof refreshToken === "string") {
    await getPlatform().secureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * DUE-221 (Task 10): exchanges the one-time code minted by the bot's
 * `/admin_web` command for a normal JWT + refresh-token session. Persists
 * the rotated refresh token to secure storage but does NOT touch the
 * session store — `AuthCallbackPage` (Task 12) owns calling
 * `useSessionStore.getState().setSession()` once it has the envelope.
 */
export async function exchangeAdminLoginCode(code: string): Promise<SessionEnvelope> {
  const { status, ok, body } = await rawFetch("/api/auth/admin-web/exchange", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!ok) {
    throw new ApiError(status, body);
  }
  const envelope = parseEnvelope(body);
  await persistRefreshToken(envelope.refresh_token);
  return envelope;
}

/**
 * DUE-221 (Task 10): refresh-token-only reauth for the browser admin.
 * Unlike `deviceAuthenticate`, there is no device-guest fallback — the
 * browser admin has neither Telegram `initData` nor a device identity to
 * mint one from, and minting one would silently hand out an anonymous
 * session on an admin surface. A dead (or absent) refresh token must
 * surface as `UnauthorizedError` so the admin shell routes to `/login`.
 *
 * - No stored token: throws immediately, no network call.
 * - Stored token + 200: rotated token persisted, envelope returned.
 * - Stored token + 401: token is dead — dropped from storage, throws
 *   `UnauthorizedError`.
 * - Any other failure (network error, non-401 status): rethrown/wrapped
 *   as-is rather than treated as "dead token", so a flaky connection
 *   doesn't get the user logged out.
 */
export async function adminWebAuthenticate(): Promise<SessionEnvelope> {
  const { secureStorage } = getPlatform();

  const storedRefreshToken = await secureStorage.getItem(REFRESH_TOKEN_KEY);
  if (!storedRefreshToken) {
    throw new UnauthorizedError();
  }

  const { status, ok, body } = await rawFetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: storedRefreshToken }),
  });
  if (ok) {
    const envelope = parseEnvelope(body);
    await persistRefreshToken(envelope.refresh_token);
    return envelope;
  }
  if (status === 401) {
    await secureStorage.removeItem(REFRESH_TOKEN_KEY);
    throw new UnauthorizedError();
  }
  throw new ApiError(status, body);
}

/** Wipes the browser-admin's persisted refresh token (logout). */
export async function clearAdminWebCredentials(): Promise<void> {
  await getPlatform().secureStorage.removeItem(REFRESH_TOKEN_KEY);
}
